export interface MentionSuggestion {
  id: string;
  displayName: string;
  avatarUrl?: string | null;
}

export interface MentionDraft {
  userId: string;
  displayName: string;
  mentionText: string;
}

export interface ActiveMentionQuery {
  query: string;
  start: number;
  end: number;
}

const MENTION_BOUNDARY_PATTERN = /[\s([{]/;

export const buildMentionText = (displayName: string) =>
  `@${displayName.replace(/\s+/g, " ").trim()}`;

export const getTrailingMentionQuery = (
  text: string | null | undefined,
  mentions: MentionDraft[] = [],
): ActiveMentionQuery | null => {
  const value = text ?? "";
  const lineStart = value.lastIndexOf("\n") + 1;

  for (let index = value.length - 1; index >= lineStart; index -= 1) {
    if (value[index] !== "@") {
      continue;
    }

    if (index > 0 && !MENTION_BOUNDARY_PATTERN.test(value[index - 1] ?? "")) {
      continue;
    }

    const query = value.slice(index + 1);
    if (query.length > 120) {
      return null;
    }

    if (mentions.some((mention) => value.slice(index).startsWith(mention.mentionText))) {
      return null;
    }

    return {
      query,
      start: index,
      end: value.length,
    };
  }

  return null;
};

export const appendMentionTrigger = (text: string) => {
  const trimmed = text.trimEnd();
  return `${trimmed}${trimmed ? " " : ""}@`;
};

export const retainMentionDraftsForText = (
  text: string,
  mentions: MentionDraft[],
): MentionDraft[] => {
  const next: MentionDraft[] = [];
  const seen = new Set<string>();

  mentions.forEach((mention) => {
    if (!mention.userId || seen.has(mention.userId)) {
      return;
    }

    if (!text.includes(mention.mentionText)) {
      return;
    }

    seen.add(mention.userId);
    next.push(mention);
  });

  return next;
};

export const insertMentionSuggestion = (
  text: string,
  mentions: MentionDraft[],
  suggestion: MentionSuggestion,
) => {
  const mentionText = buildMentionText(suggestion.displayName);
  const activeQuery = getTrailingMentionQuery(text, mentions);

  const nextText = activeQuery
    ? `${text.slice(0, activeQuery.start)}${mentionText} ${text.slice(activeQuery.end).trimStart()}`
    : `${text.trimEnd()} ${mentionText} `.trimStart();

  const nextMentions = retainMentionDraftsForText(nextText, [
    ...mentions,
    {
      userId: suggestion.id,
      displayName: suggestion.displayName,
      mentionText,
    },
  ]);

  return {
    text: nextText,
    mentions: nextMentions,
  };
};

export const getMentionRecipients = (
  text: string,
  mentions: MentionDraft[],
  actorId?: string | null,
) => {
  const next = retainMentionDraftsForText(text, mentions);
  return next.filter((mention) => mention.userId && mention.userId !== actorId);
};
